import * as Sentry from "@sentry/react";
import type { Map as LeafletMap } from "leaflet";
import {
  snapshotPrintLayerAdapters,
  type PrintLayerAdapter,
  type PrintLayerAdapterFactory,
} from "./printLayerAdapterRegistry";
import type { PrintRenderContext } from "./printRenderScale";

export interface PrintStyleSession {
  apply: (context: PrintRenderContext) => Promise<void>;
  restore: () => Promise<void>;
}

const activeMaps = new WeakSet<LeafletMap>();

export const createPrintStyleSession = (
  map: LeafletMap,
  options: {
    includeRegisteredAdapters: boolean;
    extraFactories?: PrintLayerAdapterFactory[];
  }
): PrintStyleSession => {
  if (activeMaps.has(map)) {
    throw new Error("A print style session is already active for this map");
  }

  activeMaps.add(map);

  let adapters: PrintLayerAdapter[];
  try {
    adapters = [
      ...(options.includeRegisteredAdapters
        ? snapshotPrintLayerAdapters(map)
        : []),
      ...(options.extraFactories ?? []).map((factory) => factory()),
    ];
  } catch (error) {
    activeMaps.delete(map);
    throw error;
  }

  const applied: PrintLayerAdapter[] = [];
  let restored = false;

  return {
    async apply(context) {
      for (const adapter of adapters) {
        applied.push(adapter);
        await adapter.apply(context);
      }
    },
    async restore() {
      if (restored) return;

      restored = true;
      try {
        for (const adapter of [...applied].reverse()) {
          try {
            await adapter.restore();
          } catch (error) {
            Sentry.captureException(error, {
              tags: { phase: "restore", printLayerAdapter: adapter.id },
            });
          }
        }
      } finally {
        activeMaps.delete(map);
      }
    },
  };
};
